import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Printer,
  Calculator,
  Loader2,
  Calendar,
  CheckCircle,
  Clock,
  XCircle,
  Building2,
} from "lucide-react";
import { useAuth } from "../../hooks/useAuth";
import { propostaService, Proposta } from "../../services/propostaService";

const statusConfig = {
  rascunho: { label: "Rascunho", color: "bg-gray-500/20 text-gray-400", icon: Clock },
  enviada: { label: "Enviada", color: "bg-blue-500/20 text-blue-400", icon: Clock },
  aprovada: { label: "Aprovada", color: "bg-green-500/20 text-green-400", icon: CheckCircle },
  rejeitada: { label: "Rejeitada", color: "bg-red-500/20 text-red-400", icon: XCircle },
};

export function PropostaView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [proposta, setProposta] = useState<Proposta | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/area-cliente");
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (id) {
      loadProposta();
    }
  }, [id]);

  const loadProposta = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await propostaService.getById(id);
      setProposta(data);
    } catch (error) {
      console.error("Erro ao carregar proposta:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value);
  };

  const formatNumero = (numero: number, dataEmissao: string) => {
    const num = String(numero).padStart(3, "0");
    const data = new Date(dataEmissao);
    const mes = String(data.getMonth() + 1).padStart(2, "0");
    const ano = data.getFullYear();
    return `${num}-${mes}-${ano}`;
  };

  const handlePrint = () => {
    window.print();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }

  if (!proposta) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="container mx-auto px-4 text-center">
          <Calculator className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">Proposta não encontrada</h2>
          <Link to="/portal/propostas" className="text-cyan-400 hover:text-cyan-300">
            Voltar para propostas
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[proposta.status];
  const StatusIcon = status.icon;

  return (
    <>
      <Helmet>
        <title>Proposta #{formatNumero(proposta.numero, proposta.dataEmissao)} | Portal Arcgel</title>
      </Helmet>

      <div className="min-h-screen pt-20 pb-12 print:pt-0 print:pb-0 print:bg-white">
        <div className="container mx-auto px-4 print:px-8">
          {/* Header - Hidden on print */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 print:hidden"
          >
            <div className="flex items-center justify-between">
              <Link
                to="/portal/propostas"
                className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar para propostas
              </Link>
              <button
                onClick={handlePrint}
                className="flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 transition-colors"
              >
                <Printer className="w-4 h-4" />
                Imprimir
              </button>
            </div>
          </motion.div>

          {/* Proposta Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 print:bg-white print:border-gray-200 print:shadow-none"
          >
            {/* Header da Proposta */}
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8 pb-6 border-b border-slate-700/50 print:border-gray-200">
              <div>
                <h1 className="text-2xl font-bold text-white print:text-gray-900 mb-2">
                  Proposta #{formatNumero(proposta.numero, proposta.dataEmissao)}
                </h1>
                <div className="flex items-center gap-4 text-slate-400 print:text-gray-600">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    Emissão: {new Date(proposta.dataEmissao).toLocaleDateString("pt-BR")}
                  </span>
                  <span>
                    Validade: {new Date(proposta.dataValidade).toLocaleDateString("pt-BR")}
                  </span>
                </div>
              </div>
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg ${status.color}`}>
                <StatusIcon className="w-5 h-5" />
                <span className="font-medium">{status.label}</span>
              </div>
            </div>

            {/* Cliente */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-white print:text-gray-900 mb-3">Cliente</h2>
              <div className="bg-slate-900/30 rounded-lg p-4 print:bg-gray-50">
                <div className="flex items-center gap-2 text-white print:text-gray-900 font-medium mb-2">
                  <Building2 className="w-4 h-4 text-cyan-400 print:text-cyan-600" />
                  {proposta.client.nomeFantasia || proposta.client.razaoSocial}
                </div>
              </div>
            </div>

            {/* Itens */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-white print:text-gray-900 mb-3">Itens</h2>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-900/50 print:bg-gray-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 print:text-gray-600 uppercase">Descrição</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 print:text-gray-600 uppercase">Qtd</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 print:text-gray-600 uppercase">Valor Unit.</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 print:text-gray-600 uppercase">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/50 print:divide-gray-200">
                    {proposta.itens.map((item, index) => (
                      <tr key={index}>
                        <td className="px-4 py-3 text-white print:text-gray-900">{item.descricao}</td>
                        <td className="px-4 py-3 text-center text-slate-300 print:text-gray-700">{Number(item.quantidade)}</td>
                        <td className="px-4 py-3 text-right text-slate-300 print:text-gray-700">{formatCurrency(Number(item.valorUnit))}</td>
                        <td className="px-4 py-3 text-right text-white print:text-gray-900 font-medium">{formatCurrency(Number(item.subtotal))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Totais */}
            <div className="flex justify-end mb-8">
              <div className="w-full max-w-xs space-y-2">
                <div className="flex justify-between text-slate-400 print:text-gray-600">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(Number(proposta.subtotal))}</span>
                </div>
                {Number(proposta.desconto) > 0 && (
                  <div className="flex justify-between text-red-400 print:text-red-600">
                    <span>Desconto ({proposta.desconto}%):</span>
                    <span>-{formatCurrency(Number(proposta.valorDesconto))}</span>
                  </div>
                )}
                <div className="flex justify-between text-xl font-bold text-green-400 print:text-green-600 pt-2 border-t border-slate-700/50 print:border-gray-200">
                  <span>Total:</span>
                  <span>{formatCurrency(Number(proposta.valorTotal))}</span>
                </div>
              </div>
            </div>

            {/* Observações e Condições */}
            {(proposta.observacoes || proposta.condicoesPagto) && (
              <div className="grid md:grid-cols-2 gap-6">
                {proposta.observacoes && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-400 print:text-gray-600 uppercase mb-2">Observações</h3>
                    <p className="text-white print:text-gray-900 whitespace-pre-wrap">{proposta.observacoes}</p>
                  </div>
                )}
                {proposta.condicoesPagto && (
                  <div>
                    <h3 className="text-sm font-semibold text-slate-400 print:text-gray-600 uppercase mb-2">Condições de Pagamento</h3>
                    <p className="text-white print:text-gray-900 whitespace-pre-wrap">{proposta.condicoesPagto}</p>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}
